/**
 * One shared answer to "who am I", instead of every component asking.
 *
 * `/api/auth/me` was being fetched from nine places — both route guards and
 * seven editor components — each with its own raw fetch on mount, no cache
 * and no dedupe. Two consequences, both worst on a slow connection:
 *
 *   • every guarded navigation refetched it, and the guards render nothing
 *     while it is in flight, so each route change blanked the screen for a
 *     full round trip
 *   • components that mount together (EditorShell, PresenceStack,
 *     BuildsWorkspace, CodePushSection, useTileAgent…) each fired their own,
 *     so one editor load paid for the same payload five or six times
 *
 * This module collapses that to a single request per document:
 *
 *   • concurrent callers share one in-flight promise
 *   • later callers get the resolved value synchronously via peekSession(),
 *     so a second navigation renders immediately rather than blanking
 *
 * Deliberately NOT time-based. A session here lasts as long as the document:
 * login and logout both hard-navigate (`window.location.assign`), which gives
 * a fresh module and therefore a fresh fetch. clearSession() exists for
 * anything that changes identity without a reload.
 *
 * The trade-off: if the cookie lapses while a tab stays open, this keeps
 * reporting the old user. That was already true of every call site's local
 * state, and API calls still 401 and surface through the authz banner — so
 * it trades a staleness window nobody had protection from anyway for a
 * blank screen on every navigation, which everyone saw.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface Session {
  user: SessionUser;
  /** Raw session JWT — for Bearer-only cross-origin Tile services. */
  token: string;
}

/** `undefined` = never fetched; `null` = fetched, not signed in. */
let cached: Session | null | undefined;
let inflight: Promise<Session | null> | null = null;

/**
 * The session if it is already known, without starting a request.
 *
 * Callers use this to render synchronously on the first paint — which is
 * what removes the blank frame on every navigation after the first.
 */
export function peekSession(): Session | null | undefined {
  return cached;
}

/** The session, fetching once and sharing that request with any concurrent caller. */
export function getSession(): Promise<Session | null> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = fetch('/api/auth/me', { credentials: 'same-origin' })
    .then(async (res) => {
      if (!res.ok) return null;
      const json = (await res.json()) as { user?: SessionUser; token?: string };
      return json?.user ? { user: json.user, token: json.token ?? '' } : null;
    })
    // A network failure is not "signed out", but every existing call site
    // already treated it that way (bounce to /login), so keep that behaviour
    // rather than change it here.
    .catch(() => null)
    .then((session) => {
      cached = session;
      inflight = null;
      return session;
    });

  return inflight;
}

/**
 * Forget the cached session. Call after anything that changes who is signed
 * in without a page load. Logout already hard-navigates, so this is belt and
 * braces there.
 */
export function clearSession(): void {
  cached = undefined;
  inflight = null;
}

/** Force a fresh read, for the rare caller that must not trust the cache. */
export function refreshSession(): Promise<Session | null> {
  clearSession();
  return getSession();
}
